
import { createContext, useContext, useState, useCallback, useEffect } from 'react'
import useKagamiBridge from '@/hooks/useKagamiBridge'
import { produce } from "immer"
import { apiFetch } from '@/lib/api'

const EpisodeContext = createContext()

export function EpisodeProvider({ children }) {
    const [episode, setEpisode] = useState(null)
    const [episodeSaving, setEpisodeSaving] = useState(false)
    const [saveTimeouts, setSaveTimeouts] = useState({})
    const { runTaskAndPoll, taskStatus, taskState } = useKagamiBridge()
    
    const updateEpisode = (newEpisode) => {
        setEpisode(newEpisode)
    }

    const debouncedSave = useCallback((saveFunction, key, delay = 2000) => {
        // Clear existing timeout for this key
        if (saveTimeouts[key]) {
            console.log(`Clearing key: ${key}`)
            clearTimeout(saveTimeouts[key])
        }

        // Set new timeout
        const timeoutId = setTimeout(async() => {
            
            console.log(`Saving on key: ${key}`)
            try{
                await saveFunction()
            }catch(error){
                console.error('Error saving:', error)
                setEpisodeSaving(false)
            }

            // Clean up the timeout reference
            setSaveTimeouts(prev => {
                const newTimeouts = { ...prev }
                delete newTimeouts[key]
                return newTimeouts
            })
        }, delay)

        // Store the timeout ID
        setSaveTimeouts(prev => ({
            ...prev,
            [key]: timeoutId
        }))
    }, [saveTimeouts])


    const fetchEpisode = async () => {
        try {
            const response = await apiFetch(`/api/shows/${episode.show}/episodes/${episode._id}`)
            const data = await response.json()
            updateEpisode(data)
        } catch (error) {
            console.error('Error fetching episode:', error);
        } 
    }

    const addSegment = async (index) => {
        try{
                const segmentResponse = await apiFetch(`/api/shows/${episode.show}/episodes/${episode._id}/segments?index=${index || 0}`, {
                    method: 'POST',
                    body: JSON.stringify({name: 'New Segment'})
                })

                if(!segmentResponse.ok){
                    throw new Error(data.error)
                }

                const data = await segmentResponse.json()
                await fetchEpisode()
        } catch (error) {
            console.error('Error:', error)
        }
    }

    const saveSegment = useCallback(async (segment, data) => {
        try {
            setEpisodeSaving(true)
            const response = await apiFetch(`/api/shows/${segment.show}/episodes/${segment.episode}/segments/${segment._id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data)
            })
            
            if (!response.ok) {
                throw new Error('Failed to save segment')
            }
        } catch (error) {
            console.error('Error saving segment:', error)
        } finally{
            setEpisodeSaving(false)
        }
    }, [episode])

    const updateSegment = (segment, prop, value) => {
        updateEpisode({
            ...episode,
            segments: episode.segments.map(s => s._id === segment._id ? {...s, [prop]: value} : s)
        })

        // Debounce the save
        const key = `segment-${prop}-${segment._id}`
        debouncedSave(() => saveSegment(segment, {[prop]: value}), key)
    }

    const deleteSegment = async (segment) => {
        try{
            const segmentResponse = await apiFetch(`/api/shows/${episode.show}/episodes/${episode._id}/segments/${segment._id}`, {
                method: 'DELETE'
            })

            if(!segmentResponse.ok){
                throw new Error(data.error)
            }

            await fetchEpisode()

        }catch (error) {
            console.error('Error:', error)
        }
    }

    const addScene = async (segment, index) => {
        try{
                const shotResponse = await apiFetch(`/api/shows/${segment.show}/episodes/${segment.episode}/segments/${segment._id}/scenes?index=${index || 0}`, {
                    method: 'POST',
                    body: JSON.stringify({name: 'New Scene'})
                })

                if(!shotResponse.ok){
                    throw new Error(data.error)
                }

                const data = await shotResponse.json()
                await fetchEpisode()
        } catch (error) {
            console.error('Error:', error)
        }
    }

    const saveScene = useCallback(async (scene, data) => {
        try {
            setEpisodeSaving(true)
            const response = await apiFetch(`/api/shows/${scene.show}/episodes/${scene.episode}/segments/${scene.segment}/scenes/${scene._id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data)
            })
            
            if (!response.ok) {
                throw new Error('Failed to save scene')
            }
        } catch (error) {
            console.error('Error saving scene:', error)
        }finally{
            setEpisodeSaving(false)
        }
    }, [episode])

    const updateScene = async(scene, prop, debounce = true) => {
        updateEpisode({
            ...episode,
            segments: episode.segments.map(s => s._id === scene.segment ? {...s, scenes: s.scenes.map(sc => sc._id === scene._id ? {...sc, ...prop} : sc)} : s)
        })

        // Debounce the save
        const key = `scene-${scene._id}`
        if(debounce){
            debouncedSave(() => saveScene(scene, prop), key)
        }else{
            await saveScene(scene, prop)
        }
    }

    const deleteScene = async (scene) => {
        try{
            const sceneResponse = await apiFetch(`/api/shows/${scene.show}/episodes/${scene.episode}/segments/${scene.segment}/scenes/${scene._id}`, {
                method: 'DELETE'
            })

            if(!sceneResponse.ok){
                throw new Error(data.error)
            }

            await fetchEpisode()
        } catch (error) {
            console.error('Error:', error)
        }
    }

    const saveScript = useCallback(async (script, data) => {
        try {
            setEpisodeSaving(true)
            const response = await apiFetch(`/api/shows/${script.show}/episodes/${script.episode}/segments/${script.segment}/scenes/${script.scene}/script/${script._id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data)
            })
            
            if (!response.ok) {
                throw new Error('Failed to save script')
            }
        } catch (error) {
            console.error('Error saving script:', error)
        } finally{
            setEpisodeSaving(false)
        }
    }, [episode])

    const addScript = async (scene, index, data = {}) => {
        try{
            const scriptResponse = await apiFetch(`/api/shows/${scene.show}/episodes/${scene.episode}/segments/${scene.segment}/scenes/${scene._id}/script?index=${index || 0}`, {
                method: 'POST',
                body: JSON.stringify(data)
            })

            if(!scriptResponse.ok){
                throw new Error(data.error)
            }

            // const data = await scriptResponse.json()
            await fetchEpisode()
        } catch (error) {
            console.error('Error:', error)
        }
    }

    const updateScript = (script, prop, debounce = true) =>{
        updateEpisode({
            ...episode,
            segments: episode.segments.map(s => s._id === script.segment ? {...s, scenes: s.scenes.map(sc => sc._id === script.scene ? {...sc, script: sc.script.map(e => e._id === script._id ? {...e, ...prop} : e)} : sc)} : s)
        })

        if(debounce){
            // Debounce the save
            const key = `script-${script._id}`
            debouncedSave(() => saveScript(script, prop), key)
        }else{
            saveScript(script, prop)
        }
    }

    const deleteScript = async (script) => {
        try{
            const scriptResponse = await apiFetch(`/api/shows/${script.show}/episodes/${script.episode}/segments/${script.segment}/scenes/${script.scene}/script/${script._id}`, {
                method: 'DELETE'
            })

            if(!scriptResponse.ok){
                throw new Error(data.error)
            }

            await fetchEpisode()
        } catch (error) {
            console.error('Error:', error)
        }
    }

    const getWordCount = (element) =>{
        if(element){
            return element.text?.split(' ').filter(word => word.trim() !== '').length || 0
        }else {
            return episode.segments?.reduce((acc, segment) => {
                return acc + segment.scenes.reduce((acc, scene) => {
                    return acc + scene.script.reduce((acc, element) => {
                        return acc + element.text?.split(' ').filter(word => word.trim() !== '').length || 0
                    }, 0)
                }, 0)
            }, 0)
        }
    }

    const generateVoice = async (element) => {
        try{
            await runTaskAndPoll({
                function: 'generate-voice',
                showId: episode?.show,
                payload: {elementId: element._id}
            })
            await fetchEpisode()
        } catch (error) {
            console.error('Error:', error)
        }
    }

    const fetchScriptHistory = async (scriptId) => {
        let data = []
        try{
            const response = await apiFetch(`/api/shows/${episode.show}/episodes/${episode._id}/history?scriptId=${scriptId || ''}`)
            if(!response.ok){
                throw new Error('Failed to fetch history')
            }
            data = await response.json()
        }catch(error){
            console.error('Error:', error)
        }
        return data
    }

    useEffect(() => {
        return () => {
            Object.values(saveTimeouts).forEach(timeoutId => {
                clearTimeout(timeoutId)
            })
        }
    }, [saveTimeouts])

    return (
        <EpisodeContext.Provider value={{ episode, 
        updateEpisode, 
        saveSegment, 
        saveScene, 
        deleteScene, 
        addScene,
        updateScene, 
        deleteSegment, 
        fetchEpisode, 
        updateSegment, 
        addSegment, 
        episodeSaving, 
        getWordCount, 
        generateVoice, 
        addScript, 
        updateScript, 
        deleteScript, 
        fetchScriptHistory }}>
            {children}
        </EpisodeContext.Provider>
    )
}

export function useEpisode() {
    const context = useContext(EpisodeContext)
    if (!context) {
        throw new Error('useEpisode must be used within a EpisodeProvider')
    }
    return context
} 